/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  Box,
  Divider,
  Dropdown,
  Group,
  IconCaretDown,
  IconCaretUp,
  IconThreeDotsVer,
  List,
  Typography,
  Wrapper
} from '@screentone/core';
import { v4 as uuidv4 } from 'uuid';

import { CommonEntityProps } from 'components/datamodel/commonEntityProps';
import { ResponsiveLoader } from 'components/responsive-loader/ResponsiveLoader';
import { useAllessehContentUpdate } from 'contexts/allesseh-content-update/useAllessehContentUpdate';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { MenuActionsKeys } from 'contexts/context-menu-actions/ContextMenuActionsContext';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { usePageTypeSettingsContext } from 'contexts/page-type-settings/usePageTypeSettings';
import { useSummarianContext } from 'contexts/summarian/useSummarianContext';
import { ArticleItem, QueryItem, useDedupedArtifactQuery } from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { defaultSummaryHeadline } from 'utils/article';
import { safelyParseContent } from 'utils/temp';
import { decodeHtmlEntities } from 'utils/text';
import styles from './Query.module.scss';
import { CommonContentProps } from '../commonContentProps';

import { useAltSummFields } from 'contexts/summarian/summarian.utils';

interface QueryListItemProps {
  articleItem: ArticleItem;
  isSection: boolean;
}

const QueryListItem = ({ articleItem, isSection }: QueryListItemProps) => {
  const { selectedAltSummVariant: altSummVariant } = useSummarianContext();
  const parsedContent = useMemo(() => safelyParseContent<AllessehContent>(articleItem.content), [articleItem]);
  const updatedContent = useAllessehContentUpdate(articleItem.attributes.id, {
    initialContent: parsedContent
  });
  const allessehContent = updatedContent ?? parsedContent;

  const altSummFields = useAltSummFields(allessehContent, altSummVariant);
  const headline = isSection ? decodeHtmlEntities(defaultSummaryHeadline(allessehContent)) : altSummFields.headline;

  return (
    <List.Item
      key={articleItem.attributes.id}
      data-allesseh-id={articleItem.attributes.id}
      listStyle="bullet"
      margin={{ vertical: 'sm' }}
    >
      <Typography variant="bodytext" className={styles.headline}>
        {headline}
      </Typography>
    </List.Item>
  );
};

export interface QueryProps extends CommonEntityProps, CommonContentProps {
  data: QueryItem;
}

export const Query = ({
  data: query,
  availableContentSlots,
  parentHierarchyId,
  ignoreBaseQuery,
  index,
  isHistory
}: QueryProps) => {
  const {
    generateHierarchyId,
    modifyEntity,
    getDTO,
    root,
    applyDedupedRoot,
    hasModelChangedOnce,
    setIsQueryLoading
  } = useDataModelContext();
  const { renderActions, hasRenderActions } = useContextMenuActions();
  const hierarchyId = generateHierarchyId(query, parentHierarchyId, index);
  const actionsType = isHistory ? MenuActionsKeys.HistoryQuery : MenuActionsKeys.DraftQuery;
  const currentProperty = useConvertedProperty();
  const { isSection } = usePageTypeSettingsContext();

  let numItemsToShow = availableContentSlots ?? 0;
  numItemsToShow = numItemsToShow >= 0 ? numItemsToShow : 0;

  const { data: publicationSettingsResp } = usePublicationSettings();
  const baseQuery = publicationSettingsResp?.publicationSetting.baseAllessehQuery;
  const queryName = query.attributes.feedTitle;

  const artifactDTO = useMemo(() => {
    const dto = getDTO(true, true);
    return dto;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [root.collection]);

  const { data: dedupedData, isFetching: dedupedIsLoading } = useDedupedArtifactQuery(
    { dedupedArtifactInput: { artifactDTO, publicationKey: currentProperty! } },
    {
      enabled:
        !!currentProperty &&
        hasModelChangedOnce &&
        !isHistory &&
        // @ts-expect-error Query is not typed as a `AllessehContentQueryBody`
        query.attributes.query?.count > 0 &&
        numItemsToShow > 0,
      staleTime: 5000
    }
  );

  const isLoading = dedupedIsLoading;

  useLayoutEffect(() => {
    setIsQueryLoading(isLoading);
  }, [isLoading, setIsQueryLoading]);

  useEffect(() => {
    if (dedupedData && 'root' in dedupedData.dedupedArtifact) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      applyDedupedRoot(dedupedData.dedupedArtifact.root);
    }
  }, [dedupedData, applyDedupedRoot, hierarchyId]);

  const { queryItems } = query;
  const queryItemsLength = queryItems?.length ?? 0;

  useEffect(() => {
    if (!isHistory) {
      modifyEntity(
        hierarchyId,
        (entity) => {
          const query = entity as QueryItem;
          if (!query.attributes.id) {
            query.attributes.id = uuidv4();
          }
          if (query.attributes.query && availableContentSlots !== undefined) {
            query.attributes.query.count = numItemsToShow;
          }
        },
        { updateRoot: false }
      );
    }
  }, [availableContentSlots, hierarchyId, isHistory, modifyEntity, numItemsToShow]);

  const [open, setOpen] = useState(false);

  const handleClick = useCallback(() => {
    setOpen(!open);
  }, [setOpen, open]);

  const params = useMemo(
    () => ({
      entity: query,
      hierarchyId,
      index,
      isHistory,
      extraProperties: { numItemsToShow, baseQuery, ignoreBaseQuery }
    }),
    [query, hierarchyId, index, isHistory, numItemsToShow, baseQuery, ignoreBaseQuery]
  );

  return (
    <Box data-model-hierarchy-id={hierarchyId} className={styles.cardContainer}>
      <Wrapper padding={{ all: 'sm' }} className={styles.cardHeader}>
        <Typography size="md" weight="bold" data-testid="query-title">
          Automated{queryName ? `: ${queryName}` : ''}
        </Typography>
        {hasRenderActions(actionsType, params) && (
          <Dropdown
            data-testid="page-query-ellipsis-icon"
            padding={{ all: 'none' }}
            position="right"
            trigger={<IconThreeDotsVer color="asphalt" />}
          >
            {renderActions(actionsType, params)}
          </Dropdown>
        )}
      </Wrapper>
      <Divider />
      <Wrapper padding={{ all: 'sm' }} className={styles.cardHeader}>
        <Typography size="md" data-testid="query-item-count">
          {query.attributes.query?.count ?? 0} {(query.attributes.query?.count ?? 0) === 1 ? 'item' : 'items'}
        </Typography>
        <Dropdown
          open={open}
          padding={{ all: 'none' }}
          position="right"
          onToggle={handleClick}
          trigger={
            <Group gap="xs">
              <Typography variant="note" data-testid="page-query-view-items">
                View items
              </Typography>
              {open ? <IconCaretUp color="asphalt" /> : <IconCaretDown color="asphalt" />}
            </Group>
          }
        />
      </Wrapper>
      {open && (
        <>
          {isLoading && <ResponsiveLoader />}
          {!isLoading && (
            <Wrapper padding={{ right: 'sm', left: 'xs', bottom: 'sm' }}>
              {numItemsToShow <= 0 && <Typography size="sm">No items</Typography>}
              {numItemsToShow > 0 && queryItemsLength > 0 && (
                <List listStyle="bullet" data-testid="page-query-item-list">
                  {queryItems?.map((articleItem) => (
                    <QueryListItem key={articleItem.attributes.id} articleItem={articleItem} isSection={isSection} />
                  ))}
                </List>
              )}
            </Wrapper>
          )}
        </>
      )}
    </Box>
  );
};
